"use client";
import {OneToastContainer, toast} from "@/components/commons/one-toast-container";
import {Button} from "@/components/ui/button";
import {Form, FormControl, FormField, FormItem, FormLabel, FormMessage} from "@/components/ui/form";
import {Input} from "@/components/ui/input";
import {cn} from "@/lib/utils";
import {zodResolver} from "@hookform/resolvers/zod";
import Link from "next/link";
import {useRouter} from "next/navigation";
import {useState} from "react";
import {useForm} from "react-hook-form";
import {z} from "zod";
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@/components/ui/card";

const forgotSchema = z.object({
  email: z.string().email({message: "Invalid email address"}),
  otp: z.string().optional(),
  newPassword: z.string().optional(),
});

export function ForgotPasswordForm({className, ...props}: React.ComponentProps<"form">) {
  const router = useRouter();
  const [showOtpFields, setShowOtpFields] = useState(false);
  const [loading, setLoading] = useState(false);
  const form = useForm<z.infer<typeof forgotSchema>>({
    resolver: zodResolver(forgotSchema),
    defaultValues: {
      email: "",
      otp: "",
      newPassword: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof forgotSchema>) => {
    setLoading(true);
    if (!showOtpFields) {
      // Step 1: Send OTP
      try {
        const res = await fetch("/api/users/forgot-password", {
          method: "POST",
          headers: {"Content-Type": "application/json"},
          body: JSON.stringify({email: values.email}),
        });
        const data = await res.json();
        if (res.ok && data.status === "success") {
          setShowOtpFields(true);
          toast("Please check your email for the OTP.");
        } else {
          toast.error(data?.message || "Failed to send OTP");
        }
      } catch (err) {
        toast.error("Network error. Please try again.");
      } finally {
        setLoading(false);
      }
    } else {
      // Step 2: Reset Password
      try {
        const res = await fetch("/api/users/reset-password", {
          method: "POST",
          headers: {"Content-Type": "application/json"},
          body: JSON.stringify({
            email: values.email,
            otp: values.otp,
            newPassword: values.newPassword,
          }),
        });
        const data = await res.json();
        if (res.ok && data.status === "success") {
          toast("Reset successful! Redirecting to login page");
          setTimeout(() => {
            router.push("/login");
          }, 2000);
        } else {
          toast.error(data?.message || "Failed to reset password");
        }
      } catch (err) {
        toast.error("Network error. Please try again.");
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className={cn("flex flex-col gap-6", className)}>
      <OneToastContainer />
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-xl">Forgot your password?</CardTitle>
          <CardDescription>Enter your email to reset your password</CardDescription>
        </CardHeader>
        <CardContent className="pt-6 pl-6 pr-6 pb-6 w-full">
          <Form {...form}>
            <form className={cn("flex flex-col gap-6", className)} onSubmit={form.handleSubmit(onSubmit)} {...props}>
              <div className="grid gap-6">
                <FormField
                  control={form.control}
                  name="email"
                  render={({field}) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input placeholder="me@mycompany.com" type="email" {...field} disabled={loading || showOtpFields} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {showOtpFields && (
                  <>
                    <FormField
                      control={form.control}
                      name="otp"
                      render={({field}) => (
                        <FormItem>
                          <FormLabel>OTP</FormLabel>
                          <FormControl>
                            <Input placeholder="Enter OTP" {...field} disabled={loading} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="newPassword"
                      render={({field}) => (
                        <FormItem>
                          <FormLabel>New Password</FormLabel>
                          <FormControl>
                            <Input type="password" placeholder="New Password" {...field} disabled={loading} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                )}
                <Button type="submit" className="w-full" disabled={loading}>
                  {showOtpFields ? "Reset Password" : "Continue"}
                </Button>
              </div>
              <div className="text-center text-sm">
                Remembered your password?{" "}
                <Link href="/login" className="underline underline-offset-4">
                  Login
                </Link>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
